import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { clearAllRecent } from '@/features/search/recent'
import { adaptSession, usesMockAPI } from './api/backend-adapters'
import { ApiError, api } from './api/client'
import {
  changeIdentity,
  currentIdentity,
  establishIdentity,
  isCurrentIdentity,
  onIdentityChange,
} from './api/identity-state'
import type { SessionUser } from './api/types'

export const SESSION_KEY = ['session'] as const
const channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('jt-session')

export async function fetchSession(signal?: AbortSignal): Promise<SessionUser | null> {
  try {
    const { data } = await api<unknown>('/api/auth/me', { signal })
    const user = adaptSession(data)
    establishIdentity(user.id)
    return user
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null
    throw error
  }
}

async function fetchEstablishedSession(): Promise<SessionUser> {
  const user = await fetchSession()
  if (!user) throw new ApiError(401, 'UNAUTHENTICATED', '登录会话未建立，请重新登录')
  return user
}

export function useSession() {
  return useQuery({ queryKey: SESSION_KEY, queryFn: ({ signal }) => fetchSession(signal), staleTime: 30_000 })
}

export function useAccountKey(): string {
  const { data } = useSession()
  return data?.id ?? 'anon'
}

export function useSessionBroadcast() {
  const queryClient = useQueryClient()
  useEffect(() => {
    const stop = onIdentityChange((identity) => {
      clearAllRecent()
      // A successful /me transition will publish its user after this synchronous invalidation.
      void queryClient.cancelQueries({ predicate: (query) => query.queryKey[0] !== 'session' })
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'session' })
      if (!identity.accountId) queryClient.setQueryData(SESSION_KEY, null)
    })
    const onMessage = () => {
      changeIdentity(null)
      queryClient.clear()
      void queryClient.invalidateQueries({ queryKey: SESSION_KEY })
    }
    channel?.addEventListener('message', onMessage)
    return () => {
      stop()
      channel?.removeEventListener('message', onMessage)
    }
  }, [queryClient])
}

function useIdentitySwitch() {
  const queryClient = useQueryClient()
  return (user: SessionUser | null) => {
    if (!user || currentIdentity().accountId !== user.id) changeIdentity(user?.id ?? null)
    queryClient.clear()
    queryClient.setQueryData(SESSION_KEY, user)
    channel?.postMessage('changed')
  }
}

export function useLogin() {
  const switchTo = useIdentitySwitch()
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api('/api/auth/login', { method: 'POST', body: input }).then(() => {
        changeIdentity(null)
        return fetchEstablishedSession()
      }),
    onSuccess: switchTo,
  })
}

export function useRegister() {
  const switchTo = useIdentitySwitch()
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api('/api/auth/register', { method: 'POST', body: input }).then(async ({ meta }) => {
        changeIdentity(null)
        const user = await fetchEstablishedSession()
        return { user, emailStatus: usesMockAPI ? 'queued' : meta?.verificationEmailStatus }
      }),
    onSuccess: ({ user }) => switchTo(user),
  })
}

export function useClearSession() {
  const switchTo = useIdentitySwitch()
  return () => switchTo(null)
}

export function useLogoutAll() {
  const clearSession = useClearSession()
  return useMutation({
    mutationFn: () => api('/api/auth/logout-all', { method: 'POST' }),
    onSuccess: clearSession,
  })
}

export function useLogout() {
  const switchTo = useIdentitySwitch()
  return useMutation({
    onMutate: () => currentIdentity(),
    mutationFn: () => api('/api/auth/logout', { method: 'POST' }),
    onSettled: (_data, _error, _variables, identity) => {
      if (identity && isCurrentIdentity(identity)) switchTo(null)
    },
  })
}

export function isPaid(user: SessionUser | null | undefined): boolean {
  return !!user && (user.role === 'ADMIN' || user.membership.status === 'ACTIVE')
}
